import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useApp } from "@/context/AppProvider";
import { MaskLines, Magnetic } from "@/components/motion";
import { EditableText } from "@/components/editable";
import { PORTRAIT } from "@/content";
import { ArrowDown, ArrowUpRight } from "lucide-react";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export const Hero = ({ ready }) => {
  const { tr, editMode } = useApp();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const portraitY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const bgTextY = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} id="top" data-testid="hero" className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-16">
      <div className="absolute inset-0 -z-10" style={{ background: "radial-gradient(1200px 700px at 70% 20%, rgba(197,160,89,0.14), transparent 60%), radial-gradient(900px 600px at 10% 80%, rgba(240,239,234,0.9), transparent 55%)" }} />

      <motion.span style={{ y: bgTextY }} className="pointer-events-none select-none absolute -z-0 top-1/2 -translate-y-1/2 end-[-4%] font-display leading-none opacity-[0.04] hidden md:block">
        <span className="text-[22vw]" style={{ color: "var(--ink)" }}>ENG</span>
      </motion.span>

      <div className="container-lux grid lg:grid-cols-12 gap-10 items-center relative">
        <div className="lg:col-span-7 order-2 lg:order-1">
          <EditableText as="p" path={["hero", "eyebrow"]} value={tr.hero.eyebrow} className="eyebrow mb-6 inline-block" />

          {editMode ? (
            <h1 className="font-display font-medium leading-[1.02] text-[13vw] sm:text-7xl lg:text-8xl tracking-tight" style={{ color: "var(--ink)" }} data-testid="hero-name">
              <EditableText as="span" path={["hero", "name1"]} value={tr.hero.name1} className="block" />
              <EditableText as="span" path={["hero", "name2"]} value={tr.hero.name2} className="block text-gradient-gold" />
            </h1>
          ) : (
            <h1 className="font-display font-medium leading-[1.02] text-[13vw] sm:text-7xl lg:text-8xl tracking-tight" style={{ color: "var(--ink)" }} data-testid="hero-name">
              <MaskLines lines={[tr.hero.name1]} animate={ready} delay={0.25} />
              <MaskLines lines={[tr.hero.name2]} animate={ready} delay={0.42} lineClass="text-gradient-gold" />
            </h1>
          )}

          <motion.div initial={{ opacity: 0, y: 24 }} animate={ready ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.9, delay: 0.7 }}>
            <EditableText as="p" multiline path={["hero", "tagline"]} value={tr.hero.tagline} className="mt-8 max-w-xl text-lg md:text-xl leading-relaxed font-body block" style={{ color: "var(--ink-soft)" }} />
            <EditableText as="p" multiline path={["hero", "sub"]} value={tr.hero.sub} className="mt-3 max-w-xl text-base font-body italic block" style={{ color: "var(--ink)" }} />
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 24 }} animate={ready ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.9, delay: 1 }} className="mt-10 flex flex-wrap items-center gap-4">
            <Magnetic>
              <button data-testid="hero-cta-contact" onClick={() => scrollTo("contact")} className="inline-flex items-center gap-2 rounded-full px-7 py-4 text-white text-sm font-body font-medium" style={{ backgroundColor: "var(--ink)" }}>
                <EditableText path={["hero", "cta1"]} value={tr.hero.cta1} />
                <ArrowUpRight size={17} />
              </button>
            </Magnetic>
            <Magnetic>
              <button data-testid="hero-cta-experience" onClick={() => scrollTo("experience")} className="inline-flex items-center gap-2 rounded-full px-7 py-4 text-sm font-body font-medium" style={{ border: "1px solid var(--ink)", color: "var(--ink)" }}>
                <EditableText path={["hero", "cta2"]} value={tr.hero.cta2} />
              </button>
            </Magnetic>
          </motion.div>
        </div>

        <div className="lg:col-span-5 order-1 lg:order-2 flex justify-center">
          <motion.div style={{ y: portraitY, opacity: fade }} className="relative">
            <div className="absolute inset-0 -z-10 blur-3xl rounded-full scale-110" style={{ background: "radial-gradient(circle at 50% 40%, rgba(197,160,89,0.35), transparent 65%)" }} />
            <motion.div
              initial={{ clipPath: "inset(100% 0 0 0)" }}
              animate={ready ? { clipPath: "inset(0% 0 0 0)" } : {}}
              transition={{ duration: 1.2, delay: 0.5, ease: [0.76, 0, 0.24, 1] }}
              className="relative w-[74vw] max-w-[380px] overflow-hidden"
              style={{ borderRadius: "220px 220px 28px 28px", backgroundColor: "var(--sand)" }}
            >
              <img src={PORTRAIT} alt="Ali Salem Abdullah Inkis" data-testid="hero-portrait" className="w-full h-auto object-cover object-top" loading="eager" />
            </motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={ready ? { opacity: 1, scale: 1 } : {}} transition={{ duration: 0.7, delay: 1.4 }} className="absolute -bottom-4 start-[-8%] glass rounded-2xl px-5 py-3 shadow-soft">
              <p className="font-display text-2xl" style={{ color: "var(--ink)" }}>PMP®</p>
              <p className="text-[11px] tracking-widest uppercase" style={{ color: "var(--ink-soft)" }}>Certified</p>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.div initial={{ opacity: 0 }} animate={ready ? { opacity: 1 } : {}} transition={{ delay: 1.6, duration: 0.8 }} className="absolute bottom-8 inset-x-0 flex justify-center">
        <div className="flex flex-col items-center gap-2" style={{ color: "var(--ink-soft)" }}>
          <span className="text-[11px] tracking-[0.3em] uppercase">{tr.hero.scroll}</span>
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
            <ArrowDown size={16} style={{ color: "var(--gold)" }} />
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};
